import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { produce } from 'immer';

import { gqlClient } from '@/api/graphql-client';
import meKeys from '@/api/queries/me/keyFactory';
import { graphql } from '@/gql';
import {
  GetMeQuery,
  UpdateMyPreferencesInput,
  UpdateMyPreferencesMutation as UpdateMyPreferencesMutationResult,
} from '@/gql/graphql';

const UpdateMyPreferencesMutation = graphql(`
  mutation UpdateMyPreferences($input: UpdateMyPreferencesInput!) {
    updateMyPreferences(input: $input) {
      useNewDashboard
      collapsedScheduleGroupIds
    }
  }
`);

export const useUpdateMyPreferences = (args?: {
  options?: UseMutationOptions<
    UpdateMyPreferencesMutationResult,
    Error,
    UpdateMyPreferencesInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: UpdateMyPreferencesInput) => {
      return gqlClient.request(UpdateMyPreferencesMutation, {
        input,
      });
    },
    onSuccess: async (data, variables, context) => {
      // Write the confirmed preferences into the cache rather than waiting
      // for the invalidation refetch: components acting on a preference
      // (e.g. Proposal-card routing) hold the rendered value, which stays
      // stale until the cache changes. Cancel any in-flight GetMe fetch
      // first so a response that predates the mutation can't land afterwards
      // and restore the old value.
      await queryClient.cancelQueries({ queryKey: meKeys.all, exact: true });
      queryClient.setQueryData<GetMeQuery>(
        meKeys.all,
        (cached) =>
          cached &&
          produce(cached, (draft) => {
            draft.me.preferences = {
              ...draft.me.preferences,
              ...data.updateMyPreferences,
            };
          })
      );
      void queryClient.invalidateQueries({ queryKey: meKeys.all });
      await onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};
